import { createReducer } from '@reduxjs/toolkit';
import { CompData } from '../../RecordingSessionDataGrid/types';
import {
    DefaultDraftSession,
    DefaultEpisodeData,
    type DraftRecordingSession,
    EpisodeData, type SelectInfoItem,
    TakeGroup,
} from '../types';
import {
    setCharacterOptions,
    setComps, setDraftSession, setIsDraft,
    setEpisodeData,
    setEpisodeOptions, setFilter,
    setSeasonOptions, setTakeGroups, setSubTakeIdToFocus,
    toggleScriptView, setTakeRecordingOrder, setTakeCharacterIds, resetState, setSelectInfos,
    setLineRevisionChains, toggleCompTextExpanded,
} from './actions';
import { addComp, addCompTake, altComp, circleComp, removeComp, removeCompTake, updateCompName } from './compThunks';
import { pendingActions, rejectedActions } from './optionsThunks';
import {
    addTake,
    addTakeGroup,
    toggleCircleTake,
    deleteTake,
} from './takeThunks';

export type RecordingSessionState = {
    draftSession: DraftRecordingSession
    isDraft: boolean
    seasonOptions: {
        options: Record<string, string>
        loading: boolean
    }
    episodeOptions: {
        options: Record<string, string>
        loading: boolean
    }
    characterOptions: {
        options: Record<string, string>
        loading: boolean
    }
    error?: string

    episodeData: EpisodeData
    takeGroups: TakeGroup[]
    comps: CompData[]
    selectInfos: SelectInfoItem[]

    scriptView: boolean
    subTakeIdToFocus?: string

    takeCharacterIds: string[]
    takeRecordingOrder: 'ByCharacter' | 'Chronologically'

    /** Maps lineId (e.g. LINE-xxx) to its Firestore revisionChainId */
    lineRevisionChains: Record<string, string>

    /**
     * Comp ids whose text is expanded (more/less) in CompDetail. Lifted out of that component's
     * local state so it survives Printable's duplicate render of the tree into the print iframe
     * (a separate mount with its own component state otherwise starts collapsed).
     */
    expandedCompTextIds: string[]
};

const initialState: RecordingSessionState = {
    draftSession: DefaultDraftSession,
    isDraft: true,
    seasonOptions: {
        options: {},
        loading: false,
    },
    episodeOptions: {
        options: {},
        loading: false,
    },
    characterOptions: {
        options: {},
        loading: false,
    },

    episodeData: DefaultEpisodeData,
    takeGroups: [],
    comps: [],
    selectInfos: [],

    scriptView: false,

    takeCharacterIds: [],
    takeRecordingOrder: 'ByCharacter',
    lineRevisionChains: {},
    expandedCompTextIds: [],
}

const reducer = createReducer(initialState, builder => {
    builder
        .addCase(resetState, () => initialState)

        .addCase(setDraftSession, (state, action) => {
            state.draftSession = {
                ...action.payload,
                filter: state.draftSession.filter,
            };
        })
        .addCase(setFilter, (state, action) => {
            state.draftSession.filter = action.payload;
        })
        .addCase(setSeasonOptions, (state, action) => {
            state.seasonOptions.options = action.payload;
            state.seasonOptions.loading = false;
        })
        .addCase(setEpisodeOptions, (state, action) => {
            state.episodeOptions.options = action.payload;
            state.episodeOptions.loading = false;
        })
        .addCase(setCharacterOptions, (state, action) => {
            state.characterOptions.options = action.payload;
            state.characterOptions.loading = false;
        })

        .addCase(setEpisodeData, (state, action) => {
            state.episodeData = action.payload;
        })
        .addCase(setComps, (state, action) => {
            state.comps = action.payload;
        })
        .addCase(setTakeGroups, (state, action) => {
            state.takeGroups = action.payload;
        })
        .addCase(setSelectInfos, (state, action) => {
            state.selectInfos = action.payload;
        })

        .addCase(toggleScriptView, (state) => {
            state.scriptView = !state.scriptView;
        })
        .addCase(setSubTakeIdToFocus, (state, action) => {
            state.subTakeIdToFocus = action.payload;
        })

        .addCase(setTakeCharacterIds, (state, action) => {
            state.takeCharacterIds = action.payload;
        })
        .addCase(setTakeRecordingOrder, (state, action) => {
            state.takeRecordingOrder = action.payload;
        })
        .addCase(setLineRevisionChains, (state, action) => {
            state.lineRevisionChains = action.payload;
        })
        .addCase(setIsDraft, (state, action) => {
            state.isDraft = action.payload;
        })
        .addCase(toggleCompTextExpanded, (state, action) => {
            const id = action.payload;
            const idx = state.expandedCompTextIds.indexOf(id);
            if (idx === -1) state.expandedCompTextIds.push(id);
            else state.expandedCompTextIds.splice(idx, 1);
        })

    builder
        .addCase(addTake.rejected, (state, action) => {
            state.error = action.error?.message ?? action.payload ?? 'Unknown error';
        })
        .addCase(deleteTake.rejected, (state, action) => {
            state.error = action.error?.message ?? action.payload ?? 'Unknown error';
        })
        .addCase(addTakeGroup.rejected, (state, action) => {
            state.error = action.error?.message ?? action.payload ?? 'Unknown error';
        })
        .addCase(toggleCircleTake.rejected, (state, action) => {
            state.error = action.error?.message ?? action.payload ?? 'Unknown error';
        })
        .addCase(addComp.rejected, (state, action) => {
            state.error = action.error?.message ?? action.payload ?? 'Unknown error';
        })
        .addCase(removeComp.rejected, (state, action) => {
            state.error = action.error?.message ?? action.payload ?? 'Unknown error';
        })
        .addCase(addCompTake.rejected, (state, action) => {
            state.error = action.error?.message ?? action.payload ?? 'Unknown error';
        })
        .addCase(removeCompTake.rejected, (state, action) => {
            state.error = action.error?.message ?? action.payload ?? 'Unknown error';
        })
        .addCase(circleComp.rejected, (state, action) => {
            state.error = action.error?.message ?? action.payload ?? 'Unknown error';
        })
        .addCase(altComp.rejected, (state, action) => {
            state.error = action.error?.message ?? action.payload ?? 'Unknown error';
        })
        .addCase(updateCompName.fulfilled, (state, action) => {
            const comp = state.comps.find(c => c.id === action.payload.compId);
            if (comp) {
                comp.name = action.payload.name;
            }
        })

    pendingActions.map((tuple ) => {
        builder.addCase(tuple[0], (state) => {
            state[tuple[1]].loading = true;
        })
    });
    rejectedActions.map((tuple) => {
        builder.addCase(tuple[0], (state, action) => {
            state[tuple[1]].loading = false;
            state.error = action.error.message;
        })
    });
});

export default reducer;